import type {ReactElement} from 'react';
import {Button, Size, Type} from '@Components/button';
import {removeDuplicateColorPalettes} from '@Libraries/auto-branding-email-maker';
import {openBrandKitUpdateFromColorPaletteModal, PANEL_ID} from '@Modals/brand-kit-update-from-color-palette-modal';
import {ColorPalette} from '@Components/color-palettes/color-palette/color-palette';
import type {ColorPaletteInfo} from '@Libraries/color-palettes.library';
import {addPaletteToRecents, isSameColorPalette} from '@Libraries/color-palettes.library';
import {ColorPalettesContainer} from '@Components/color-palettes/color-palettes-container';
import {setColorPaletteBeingApplied} from '@/reducers/color-palettes.reducer';
import {useAppDispatch, useAppSelector} from '@/hooks';
import type {BrandKitPaletteEditorProps} from './brand-kit-palette-editor.types';
import styles from './brand-kit-palette-editor.module.scss';

export function BrandKitPaletteEditor({brandKitPalette, suggestedPalettes, onPaletteSelect, onBrandKitUpdate, colorPaletteAppliedEvent}: BrandKitPaletteEditorProps): ReactElement | null {
  const dispatch = useAppDispatch();

  const selectedColorPalette = useAppSelector((state) => {
    return state.colorPalettes.selectedColorPalette ?? null;
  });

  if (!selectedColorPalette) {
    return null;
  }

  const handlePaletteSelection = (colorPalette: ColorPaletteInfo): void => {
    void addPaletteToRecents(colorPalette);

    onPaletteSelect(colorPalette);
  };

  const openUpdateBrandKitModal = (): void => {
    openBrandKitUpdateFromColorPaletteModal({
      panelId: PANEL_ID,
      currentColorPalette: brandKitPalette,
      newColorPalette: selectedColorPalette,
      onBrandKitUpdate,
    });
  };

  const renderUpdateBrandKitButton = (): ReactElement | null => {
    if (isSameColorPalette(selectedColorPalette, brandKitPalette)) {
      return null;
    }

    return (
      <div className={styles.background}>
        <Button type={Type.PRIMARY} size={Size.SMALL} text={window.i18next.t('pmwjs_update_brand_kit')} textClasses="_text-transform-none" onClick={openUpdateBrandKitModal} />
      </div>
    );
  };

  const onColorPaletteDoneApplying = (): void => {
    dispatch(setColorPaletteBeingApplied(undefined));
  };

  return (
    <>
      <div className="spacing-m-t-3 spacing-m-b-6">
        <ColorPalette colorPaletteAppliedEvent={colorPaletteAppliedEvent} palette={selectedColorPalette} isSelected />
      </div>
      {renderUpdateBrandKitButton()}
      <ColorPalettesContainer
        onColorPaletteDoneApplying={onColorPaletteDoneApplying}
        useUniqueStringAsKeyForPalette
        colorPaletteAppliedEvent={colorPaletteAppliedEvent}
        heading={window.i18next.t('pmwjs_more_suggestions')}
        colorPalettes={removeDuplicateColorPalettes(suggestedPalettes)}
        onColorPaletteSelected={handlePaletteSelection}
        showPaletteName={false}
        showNameOverlayOnHover={false}
      />
    </>
  );
}
